// File: src/pages/ReferralPartnerDashboardPage.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

import {
  LuArrowLeft, LuCheck, LuCopy, LuExternalLink, LuLightbulb,
  LuMessageCircle, LuPartyPopper, LuRefreshCw, LuSearch, LuStore, LuUsers,
} from 'react-icons/lu';

const API = process.env.REACT_APP_API_URL || '';

export default function ReferralPartnerDashboardPage() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext) || {};
  const userId = auth.currentUser?._id || auth.currentUser?.id || localStorage.getItem('userId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const requestRef = useRef(0);
  const mountedRef = useRef(false);
  const refreshRef = useRef(null);

  async function loadDashboard() {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestRef.current;
    setRefreshing(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setData(null);
        setError('Please sign in to view your creator dashboard.');
        return;
      }
      const response = await axios.get(`${API}/referral-partners/me`, {
        headers: { Authorization: `Bearer ${token}` },
        params: userId ? { userId } : undefined,
        signal: controller.signal,
      });
      if (!mountedRef.current || requestRef.current !== requestId) return;
      const payload = response.data;
      if (!payload || !payload.partner || !Array.isArray(payload.referrals)) throw new Error('Unexpected dashboard response');
      setData(payload);
    } catch (err) {
      if (controller.signal.aborted || !mountedRef.current || requestRef.current !== requestId) return;
      if ([401, 403].includes(err?.response?.status)) {
        setData(null);
        setError(err.response.status === 401 ? 'Please sign in to view your creator dashboard.' : 'This account does not have access to the creator dashboard.');
      } else {
        setError('We couldn’t refresh your referrals. Please try again.');
      }
    } finally {
      if (mountedRef.current && requestRef.current === requestId) { setLoading(false); setRefreshing(false); }
    }
  }
  refreshRef.current = loadDashboard;

  useEffect(() => {
    mountedRef.current = true;
    setData(null);
    setLoading(true);
    refreshRef.current();
    const onFocus = () => { refreshRef.current(); };
    window.addEventListener('focus', onFocus);
    return () => { mountedRef.current = false; controllerRef.current?.abort(); window.removeEventListener('focus', onFocus); };
  }, [userId]);

  async function handlePortalAction() {
    if (auth.currentUser?.isHost === true) { navigate('/portal'); return; }
    if (typeof auth.logout === 'function') await auth.logout();
    ['accessToken', 'authToken', 'token', 'userId'].forEach(key => localStorage.removeItem(key));
    window.location.assign('/events');
  }

  return <ReferralDashboard
    partner={data?.partner || {}}
    referrals={data?.referrals || []}
    contactUrl={data?.contactUrl}
    loading={loading}
    refreshing={refreshing}
    error={error}
    onRefresh={loadDashboard}
    onBack={() => navigate(-1)}
    onPortalAction={handlePortalAction}
    portalActionLabel={auth.currentUser?.isHost === true ? 'Switch portal' : 'Log out'}
    logo={<TribeLogo aria-label="Tribe" />}
  />;
}

export const REFERRAL_RATE = 3;
export const BONUS_TIERS = [
  { referrals: 25, bonus: 25 },
  { referrals: 50, bonus: 150 },
  { referrals: 100, bonus: 250 },
  { referrals: 200, bonus: 600 },
];
const TIME_ZONE = 'America/Los_Angeles';
const DEFAULT_SIGNUP_URL = 'https://www.jointribe.io';
const DEFAULT_CONTACT_URL = 'https://www.instagram.com/jointribe.io/';
const IDEAS = [
  {
    title: 'Use cold DMs to reach new people', Icon: LuMessageCircle,
    text: 'Send personal invitations on Instagram or TikTok, or start conversations on Bumble For Friends. Explain what Tribe is, why it might interest them, and offer a private invite.',
  },
  {
    title: 'Throw a party with a Tribe membership guest list', Icon: LuPartyPopper,
    text: 'Host a party, dinner, or meetup where guests sign up for Tribe to attend. Talk to the team first so we can agree on the plan and make sure your referrals are credited.',
  },
  {
    title: 'Introduce Tribe to a community', Icon: LuUsers,
    text: 'Connect with a student organization, alumni group, hobby club, or local group chat. Ask the organizer about sharing an invitation or bringing the group to Tribe together.',
  },
  {
    title: 'Pitch a local collaboration', Icon: LuStore,
    text: 'Know a café, gym, studio, or shop with a community of its own? Bring us an idea for a meetup or membership invitation that would work for their audience.',
  },
];

function validDate(value) {
  if (!value) return null;
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime()) || (dateOnly && date.toISOString().slice(0, 10) !== value)) return null;
  return date;
}

export function monthKey(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}(?:-\d{2})?$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    return year >= 1970 && month >= 1 && month <= 12 && (value.length === 7 || validDate(value)) ? value.slice(0, 7) : '';
  }
  const date = validDate(value);
  if (!date) return '';
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit' }).formatToParts(date);
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}`;
}

function earliestDate(values) {
  return values.map(validDate).filter(Boolean).sort((a, b) => a - b)[0]?.toISOString() || '';
}

function stringId(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value?._id ? String(value._id) : '';
}

function aliasesFor(row) {
  const keys = [];
  for (const field of ['personId', 'userId', 'applicantId']) {
    const value = stringId(row[field]);
    if (value) keys.push(`${field === 'applicantId' ? 'applicant' : 'person'}:${value}`);
  }
  const email = String(row.email || '').trim().toLowerCase();
  const phone = String(row.phoneNumber || row.phone || '').replace(/\D/g, '');
  if (email) keys.push(`email:${email}`);
  if (phone) keys.push(`phone:${phone}`);
  const id = stringId(row._id || row.id);
  if (id) keys.push(`record:${id}`);
  return keys;
}

function isBooked(row) {
  const reservation = row.firstEventReservation || row.reservation;
  if (['cancelled', 'canceled'].includes(String(row.status || '').toLowerCase()) && !row.attendedAt && !row.firstEventAttendedAt) return false;
  if (reservation && ['cancelled', 'canceled'].includes(String(reservation.status || '').toLowerCase())) return false;
  return Boolean(row.firstEventBooked === true || row.firstEventBookedAt || row.firstEventReservedAt || row.attendedAt || row.firstEventAttendedAt || reservation || row.status === 'event_reserved');
}

// Defensive display deduplication. The backend must supply a stable personId and
// immutable creditedAt across application -> account creation for durable credit.
export function normalizeReferrals(input = []) {
  const groups = new Set();
  const byAlias = new Map();
  for (const row of Array.isArray(input) ? input : []) {
    if (!row || typeof row !== 'object' || row.isDuplicate === true || row.isSelfReferral === true) continue;
    const aliases = aliasesFor(row);
    const matches = [...new Set(aliases.map(key => byAlias.get(key)).filter(Boolean))];
    const group = matches[0] || { rows: [], aliases: new Set() };
    if (!matches.length) groups.add(group);
    for (const other of matches.slice(1)) {
      group.rows.push(...other.rows);
      other.aliases.forEach(alias => { group.aliases.add(alias); byAlias.set(alias, group); });
      groups.delete(other);
    }
    group.rows.push(row);
    aliases.forEach(alias => { group.aliases.add(alias); byAlias.set(alias, group); });
  }
  return [...groups].map((group, index) => {
    const merged = {};
    group.rows.forEach(row => Object.entries(row).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') merged[key] = value;
    }));
    const creditedAt = earliestDate(group.rows.map(row => row.firstCreditedAt || row.creditedAt || earliestDate([
      row.accountCreatedAt, row.applicationSubmittedAt, row.appliedAt, row.createdAt,
    ])));
    const accountDate = earliestDate(group.rows.map(row => row.accountCreatedAt));
    const applicationDate = earliestDate(group.rows.map(row => row.applicationSubmittedAt || row.appliedAt || ((row.kind === 'application' || row.type === 'application') ? row.createdAt : '')));
    const hasAccount = Boolean(accountDate);
    const applied = group.rows.some(row => row.applicationSubmittedAt || row.appliedAt || row.kind === 'application' || row.type === 'application');
    return {
      ...merged,
      displayId: [...group.aliases][0] || `referral-${index}`,
      displayName: merged.name || [merged.firstName, merged.lastName || (merged.lastInitial ? `${merged.lastInitial}.` : '')].filter(Boolean).join(' ') || 'Tribe member',
      profilePicUrl: merged.profilePicUrl || merged.profilePhotoUrl || merged.avatarUrl || '',
      creditedAt,
      sourceLabel: applied && (!hasAccount || (applicationDate && applicationDate <= accountDate)) ? 'Applied' : 'Account created',
      hasBookedFirstEvent: isBooked(merged),
    };
  }).sort((a, b) => (validDate(b.creditedAt)?.getTime() || 0) - (validDate(a.creditedAt)?.getTime() || 0));
}

export function bonusForCount(count) {
  const safeCount = Math.max(0, Math.floor(Number(count) || 0));
  const reached = BONUS_TIERS.filter(tier => safeCount >= tier.referrals).slice(-1)[0] || null;
  const next = BONUS_TIERS.find(tier => safeCount < tier.referrals) || null;
  return { reached, next, bonus: reached?.bonus || 0, remaining: next ? next.referrals - safeCount : 0 };
}

export function buildMonthOptions(partner, rows, today = new Date()) {
  const current = monthKey(today);
  const keys = [monthKey(partner?.joinedAt), ...rows.map(row => monthKey(row.creditedAt))].filter(key => key && key <= current);
  const first = keys.sort()[0] || current;
  const result = [];
  for (let cursor = current; cursor >= first;) {
    result.push(cursor);
    let [year, month] = cursor.split('-').map(Number);
    month -= 1;
    if (month === 0) { year -= 1; month = 12; }
    cursor = `${year}-${String(month).padStart(2, '0')}`;
  }
  return result;
}

function money(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}
function monthLabel(key) {
  const [year, month] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 15)));
}
function dateLabel(value) {
  const date = validDate(value);
  return date ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: TIME_ZONE }).format(date) : '—';
}
function safeLink(value, fallback) {
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : fallback; } catch { return fallback; }
}

function Avatar({ referral }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [referral.profilePicUrl]);
  const initials = referral.displayName.split(/\s+/).slice(0, 2).map(name => name[0] || '').join('').toUpperCase();
  return referral.profilePicUrl && !failed ? (
    <img className="cr-avatar" src={referral.profilePicUrl} alt="" width="38" height="38" loading="lazy" onError={() => setFailed(true)} />
  ) : <span className="cr-avatar cr-avatar-fallback" aria-hidden="true">{initials}</span>;
}

export function ReferralDashboard({
  partner = {}, referrals = [], loading = false, refreshing = false, error = '',
  onRefresh, onBack, onPortalAction, portalActionLabel = '', logo = null,
  signupUrl = DEFAULT_SIGNUP_URL, contactUrl = DEFAULT_CONTACT_URL, today,
}) {
  const [tab, setTab] = useState('referrals');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [search, setSearch] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const tabRefs = useRef([]);
  const copyTimer = useRef(null);
  const now = today || new Date();
  const rows = useMemo(() => normalizeReferrals(referrals), [referrals]);
  const options = useMemo(() => buildMonthOptions(partner, rows, now), [partner.joinedAt, rows, monthKey(now)]);
  const period = options.includes(selectedMonth) ? selectedMonth : options[0];
  const monthRows = useMemo(() => rows.filter(row => monthKey(row.creditedAt) === period), [rows, period]);
  const count = monthRows.length;
  const bonus = bonusForCount(count);
  const commission = count * REFERRAL_RATE;
  const filteredRows = monthRows.filter(row => row.displayName.toLowerCase().includes(search.trim().toLowerCase()));
  const code = String(partner.referralCode || '');
  const inviteUrl = safeLink(partner.signupUrl || partner.inviteLink || signupUrl, DEFAULT_SIGNUP_URL);
  const teamUrl = safeLink(contactUrl, DEFAULT_CONTACT_URL);
  const missingDates = rows.some(row => !row.creditedAt);
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  async function copy(value, successMessage) {
    if (!value) return;
    clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(successMessage);
    } catch {
      setCopyMessage('Couldn’t copy. You can select and copy your code manually.');
    }
    copyTimer.current = setTimeout(() => setCopyMessage(''), 2500);
  }

  function changeTab(event, index) {
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : ['ArrowLeft', 'ArrowRight'].includes(event.key) ? 1 - index : null;
    if (next === null) return;
    event.preventDefault();
    setTab(next === 0 ? 'referrals' : 'ideas');
    tabRefs.current[next]?.focus();
  }

  return (
    <div className="cr-page">
      <style>{STYLES}</style>
      <div className="cr-shell">
        <header className="cr-header">
          <div className="cr-header-left">{onBack ? <button className="cr-text-button" type="button" onClick={onBack}><LuArrowLeft size={16} aria-hidden="true" />Back</button> : null}</div>
          <div className="cr-logo">{logo || <span>tribe</span>}</div>
          <div className="cr-header-right">{onPortalAction && portalActionLabel ? <button className="cr-text-button" type="button" onClick={onPortalAction}>{portalActionLabel}</button> : null}</div>
        </header>

        <main>
          <div className="cr-tabs" role="tablist" aria-label="Creator dashboard">
            {[
              { id: 'referrals', label: 'My referrals' },
              { id: 'ideas', label: 'Did you know?' },
            ].map((item, index) => (
              <button key={item.id} ref={node => { tabRefs.current[index] = node; }} type="button" role="tab" id={`cr-tab-${item.id}`} aria-controls={`cr-panel-${item.id}`} aria-selected={tab === item.id} tabIndex={tab === item.id ? 0 : -1} onClick={() => setTab(item.id)} onKeyDown={event => changeTab(event, index)}>{item.label}</button>
            ))}
          </div>

          <section id="cr-panel-referrals" role="tabpanel" aria-labelledby="cr-tab-referrals" hidden={tab !== 'referrals'} tabIndex={0} className="cr-panel">
            <div className="cr-title-row">
              <div><h1>Your referrals</h1></div>
              <div className="cr-period-controls">
                <label className="cr-month-label"><span className="cr-sr-only">Referral month</span><select aria-label="Referral month" value={period} disabled={loading} onChange={event => { setSelectedMonth(event.target.value); setSearch(''); }}>{options.map(key => <option key={key} value={key}>{monthLabel(key)}</option>)}</select></label>
                {onRefresh ? <button type="button" className="cr-icon-button" aria-label="Refresh referrals" disabled={loading || refreshing} onClick={onRefresh}><LuRefreshCw size={16} aria-hidden="true" /></button> : null}
              </div>
            </div>

            {error ? <div role="alert" className="cr-error">{error}{onRefresh ? <button type="button" className="cr-text-button" onClick={onRefresh} disabled={refreshing}>Try again</button> : null}</div> : null}

            <div className="cr-data" aria-busy={loading} hidden={Boolean(error && !code && !rows.length && !loading)}>
              <section className="cr-code-strip" aria-labelledby="cr-code-title">
                <div><h2 id="cr-code-title">Your invite code</h2><span className="cr-code" aria-label={code ? `Invite code ${code}` : 'Invite code unavailable'}>{loading ? 'Loading…' : code || 'Not available yet'}</span><p>Share your code privately so people can credit you.</p></div>
                <div className="cr-code-actions">
                  <button className="cr-primary" type="button" disabled={loading || !code} onClick={() => copy(code, 'Invite code copied.')}><LuCopy size={14} aria-hidden="true" />Copy code</button>
                  <button className="cr-text-button" type="button" disabled={loading || !code} onClick={() => copy(inviteUrl, 'Signup link copied.')} >Copy signup link</button>
                </div>
              </section>
              <div className="cr-copy-status" role="status">{copyMessage}</div>

              <section className="cr-summary" aria-label="Monthly referral results">
                <div><h2>Referrals this month</h2><strong className="cr-number">{loading ? '—' : count.toLocaleString('en-US')}</strong><p>Accounts created or applications submitted</p></div>
                <div><h2>Referral earnings</h2><strong className="cr-number">{loading ? '—' : money(commission + bonus.bonus)}</strong><p>{loading ? 'Loading your results…' : <>{money(commission)} commission + {money(bonus.bonus)} bonus</>}</p></div>
              </section>

              <section className="cr-bonus-section" aria-labelledby="cr-bonus-title">
                <div className="cr-section-title-row"><h2 id="cr-bonus-title">Monthly bonus</h2><span className="cr-bonus-current">{loading ? '—' : `${money(bonus.bonus)} reached`}</span></div>
                <p className="cr-next-bonus">{loading ? 'Loading bonus progress…' : bonus.next ? <><strong>{bonus.remaining} more {bonus.remaining === 1 ? 'referral' : 'referrals'}</strong> to unlock a {money(bonus.next.bonus)} bonus.</> : 'You’ve reached the highest monthly bonus.'}</p>
                <div className="cr-progress" role="progressbar" aria-label="Progress toward next monthly bonus" aria-valuemin={0} aria-valuemax={bonus.next?.referrals || 200} aria-valuenow={loading ? 0 : Math.min(count, bonus.next?.referrals || 200)} aria-valuetext={loading ? 'Loading' : bonus.next ? `${count} of ${bonus.next.referrals} referrals` : 'Highest bonus reached'}><span style={{ width: loading ? '0%' : `${Math.min(count / (bonus.next?.referrals || 200), 1) * 100}%` }} /></div>
                <div className="cr-tiers">{BONUS_TIERS.map(tier => {
                  const reached = !loading && count >= tier.referrals;
                  return <div key={tier.referrals} className={`cr-tier${reached ? ' cr-tier-reached' : ''}`}><div>{tier.referrals} referrals {reached ? <LuCheck size={13} aria-label="Reached" /> : null}</div><strong>{money(tier.bonus)}</strong></div>;
                })}</div>
                <p className="cr-small-note">Only the highest bonus applies. Each referral earns $3, and each person counts once.</p>
              </section>

              <section className="cr-list-section" aria-labelledby="cr-list-title">
                <div className="cr-section-title-row"><h2 id="cr-list-title">Who you’ve brought to Tribe</h2><span className="cr-muted">{monthLabel(period)}</span></div>
                {monthRows.length > 5 || search ? <label className="cr-search"><LuSearch size={15} aria-hidden="true" /><input type="search" value={search} placeholder="Search referrals" aria-label="Search referrals" onChange={event => setSearch(event.target.value)} /></label> : null}
                {loading ? <p className="cr-empty" role="status">Loading your referrals…</p> : filteredRows.length ? (
                  <table className="cr-table">
                    <caption className="cr-sr-only">Your referrals for {monthLabel(period)}</caption>
                    <thead><tr><th scope="col">Member</th><th scope="col">Joined / applied</th><th scope="col">First event</th></tr></thead>
                    <tbody>{filteredRows.map(referral => <tr key={referral.displayId}>
                      <th scope="row"><div className="cr-member"><Avatar referral={referral} /><span>{referral.displayName}</span></div></th>
                      <td><time dateTime={referral.creditedAt}>{dateLabel(referral.creditedAt)}</time><span className="cr-row-note">{referral.sourceLabel}</span></td>
                      <td>{referral.hasBookedFirstEvent ? <span className="cr-booked"><LuCheck size={13} aria-hidden="true" />Booked</span> : <span className="cr-not-booked">Not yet</span>}</td>
                    </tr>)}</tbody>
                  </table>
                ) : <div className="cr-empty">{search.trim() && monthRows.length ? 'No referrals match your search.' : <><strong>No referrals yet this month.</strong><p>Share your private invite code to get started.</p></>}</div>}
                {missingDates && !loading ? <p className="cr-small-note">Some referral dates are missing. <a href={teamUrl} target="_blank" rel="noopener noreferrer">Talk to Tribe</a> to correct your records.</p> : null}
              </section>
            </div>
          </section>

          <section id="cr-panel-ideas" role="tabpanel" aria-labelledby="cr-tab-ideas" hidden={tab !== 'ideas'} tabIndex={0} className="cr-panel cr-ideas-panel">
            <div className="cr-title-row"><div><h1>Did you know?</h1><p className="cr-subtitle">Your referrals can come from more than videos and friends.</p></div></div>
            <p className="cr-ideas-intro">Every new person who successfully creates an account or applies with your code counts toward your commissions and monthly bonuses. Here are a few other ways to introduce people to Tribe.</p>
            <div className="cr-idea-list">{IDEAS.map(({ title, text, Icon }) => <article className="cr-idea" key={title}><span className="cr-idea-icon"><Icon size={20} aria-hidden="true" /></span><div><h2>{title}</h2><p>{text}</p></div></article>)}</div>
            <section className="cr-team-cta" aria-labelledby="cr-team-title"><LuLightbulb size={22} aria-hidden="true" /><h2 id="cr-team-title">Have an idea, or want to try one of these?</h2><p>Talk to the Tribe team. We’ll work through the idea together and agree on how your referrals will be credited.</p><a className="cr-primary" href={teamUrl} target="_blank" rel="noopener noreferrer">Talk to the Tribe team<LuExternalLink size={14} aria-hidden="true" /></a></section>
          </section>
        </main>
        <footer className="cr-footer">Tribe Creator Program</footer>
      </div>
    </div>
  );
}

const STYLES = `
.cr-page { min-height:100vh; padding:28px 36px; box-sizing:border-box; background:#fff; color:#171717; font-family:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; font-size:14px; line-height:1.55; -webkit-font-smoothing:antialiased; }
.cr-page *, .cr-page *::before, .cr-page *::after { box-sizing:border-box; }
.cr-page [hidden] { display:none !important; }
.cr-shell { max-width:900px; margin:0 auto; }
.cr-header { min-height:56px; display:grid; grid-template-columns:minmax(0,1fr) auto minmax(0,1fr); align-items:center; gap:16px; margin-bottom:22px; }
.cr-header-left { justify-self:start; }.cr-header-right { justify-self:end; }
.cr-logo { font-size:31px; font-weight:800; letter-spacing:-1.6px; line-height:1; }.cr-logo svg { width:86px; height:auto; display:block; }
.cr-logo svg :is(path,circle,rect,polygon,polyline,ellipse) { fill:#050505; }.cr-logo svg [fill="none"] { fill:none; }
.cr-page button,.cr-page input,.cr-page select { font:inherit; }.cr-page button,.cr-page select { cursor:pointer; }.cr-page button:disabled,.cr-page select:disabled { cursor:default; opacity:.4; }
.cr-page button:focus-visible,.cr-page a:focus-visible,.cr-page input:focus-visible,.cr-page select:focus-visible { outline:2px solid #171717; outline-offset:4px; }
.cr-page a { color:inherit; text-underline-offset:3px; }
.cr-tabs { display:flex; gap:28px; border-bottom:1px solid #e9e9e6; }
.cr-tabs button { position:relative; min-height:46px; padding:0 0 14px; border:0; background:none; color:#83837d; font-size:13px; font-weight:550; }
.cr-tabs button[aria-selected="true"] { color:#171717; }.cr-tabs button[aria-selected="true"]::after { position:absolute; content:''; bottom:-1px; left:0; right:0; height:2px; background:#171717; }
.cr-panel { padding-top:34px; outline:none; }
.cr-title-row { display:flex; align-items:center; justify-content:space-between; gap:20px; margin-bottom:26px; }
.cr-page h1 { margin:0; font-size:clamp(27px,3vw,34px); line-height:1.12; font-weight:650; letter-spacing:-.04em; }
.cr-page h2 { margin:0; font-size:14px; line-height:1.45; font-weight:650; letter-spacing:-.01em; }
.cr-page p { margin:0; }.cr-page .cr-subtitle { margin-top:9px; color:#7a7a74; font-size:13px; }
.cr-period-controls { display:flex; align-items:center; gap:10px; }.cr-month-label select { max-width:180px; height:38px; padding:0 30px 0 11px; background:#fff; border:1px solid #e3e3de; border-radius:6px; color:#555550; font-size:12px; }
.cr-icon-button { width:36px; height:36px; display:grid; place-items:center; border:0; border-radius:6px; color:#73736e; background:none; }.cr-icon-button:hover { background:#f5f5f2; }
.cr-text-button { display:inline-flex; justify-content:center; align-items:center; gap:7px; padding:7px 0; border:0; background:none; color:#73736e; font-size:12px !important; text-decoration:none; }.cr-text-button:hover { color:#171717; }
.cr-primary { display:inline-flex; justify-content:center; align-items:center; gap:9px; min-height:40px; padding:0 18px; border:1px solid #171717; border-radius:999px; color:#fff !important; background:#171717; font-family:inherit; font-size:12px !important; font-weight:650 !important; text-decoration:none; }.cr-primary:hover { background:#333; border-color:#333; }
.cr-code-strip { display:flex; align-items:center; justify-content:space-between; gap:20px; padding:22px 24px; border:1px solid #e7e7e1; background:#f8f8f5; border-radius:10px; }
.cr-code-strip h2 { font-size:11px; font-weight:550; color:#7d7d76; }.cr-code { display:block; margin:6px 0; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:23px; font-weight:650; letter-spacing:.04em; overflow-wrap:anywhere; user-select:all; }
.cr-code-strip p { font-size:12px; color:#85857e; }.cr-code-actions { flex-shrink:0; display:flex; flex-direction:column; align-items:center; gap:4px; }
.cr-copy-status { height:23px; padding-top:4px; color:#62625d; font-size:11px; }
.cr-summary { display:grid; grid-template-columns:1fr 1fr; gap:24px; padding:20px 0 28px; border-bottom:1px solid #e9e9e4; }.cr-summary h2 { font-size:12px; color:#7a7a73; font-weight:500; }.cr-number { display:block; margin:8px 0 6px; font-size:39px; font-weight:600; line-height:1; letter-spacing:-.055em; }.cr-summary p { font-size:12px; color:#85857e; }
.cr-bonus-section { padding:26px 0; border-bottom:1px solid #e9e9e4; }.cr-section-title-row { display:flex; justify-content:space-between; align-items:baseline; gap:16px; margin-bottom:12px; }.cr-bonus-current { font-size:12px; color:#62625d; }.cr-next-bonus { color:#777770; font-size:13px; }.cr-next-bonus strong { color:#31312d; font-weight:600; }
.cr-progress { height:5px; background:#ecece7; border-radius:999px; overflow:hidden; margin:16px 0; }.cr-progress > span { display:block; height:100%; border-radius:inherit; background:#353530; transition:width 180ms ease; }
.cr-tiers { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; }.cr-tier { color:#9b9b94; }.cr-tier > div { display:flex; align-items:center; gap:6px; font-size:11px; }.cr-tier strong { display:block; margin-top:3px; font-size:16px; font-weight:550; }.cr-tier-reached { color:#30302b; }
.cr-small-note { margin-top:14px !important; color:#96968e; font-size:11px; line-height:1.55; }
.cr-list-section { padding-top:26px; }.cr-muted { font-size:11px; color:#8c8c85; }.cr-search { display:flex; align-items:center; gap:9px; height:39px; max-width:300px; margin:16px 0; padding:0 11px; color:#91918a; border:1px solid #e7e7e1; border-radius:6px; }.cr-search input { width:100%; min-width:0; padding:0; border:0; outline:none; background:none; font-size:12px; color:#33332e; }
.cr-table { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; }.cr-table th,.cr-table td { padding:15px 10px; text-align:left; vertical-align:middle; border-bottom:1px solid #eeeee8; }.cr-table th:first-child,.cr-table td:first-child { padding-left:0; width:45%; }.cr-table th:last-child,.cr-table td:last-child { padding-right:0; width:23%; text-align:right; }.cr-table thead th { padding-top:9px; padding-bottom:10px; color:#95958d; font-size:11px; font-weight:500; }.cr-table tbody th { font-weight:550; }.cr-member { display:flex; align-items:center; gap:11px; min-width:0; }.cr-member > span { overflow-wrap:anywhere; }.cr-avatar { width:38px; height:38px; flex:0 0 38px; object-fit:cover; border-radius:50%; background:#eeeee9; }.cr-avatar-fallback { display:grid; place-items:center; color:#77776f; font-size:11px; font-weight:600; }.cr-row-note { display:block; margin-top:2px; color:#a0a099; font-size:10px; }.cr-booked { display:inline-flex; align-items:center; gap:5px; color:#42423b; font-size:11px; }.cr-not-booked { color:#aaa9a0; font-size:11px; }
.cr-empty { padding:40px 16px; text-align:center; color:#95958c; font-size:13px; }.cr-empty strong { display:block; margin-bottom:6px; color:#73736a; font-weight:550; }.cr-empty p { font-size:12px; }.cr-error { display:flex; justify-content:space-between; align-items:center; gap:16px; margin-bottom:22px; padding:14px 16px; background:#fbf0ee; border:1px solid #ead6d0; border-radius:6px; color:#853f35; font-size:12px; }
.cr-ideas-panel { max-width:700px; margin:0 auto; }.cr-ideas-intro { max-width:620px; color:#7b7b73; font-size:13px; line-height:1.7; }.cr-idea-list { margin-top:30px; }.cr-idea { display:flex; align-items:flex-start; gap:16px; padding:23px 0; border-bottom:1px solid #ecece5; }.cr-idea:first-child { padding-top:0; }.cr-idea-icon { flex:0 0 38px; height:38px; display:grid; place-items:center; border-radius:9px; background:#f5f5f0; color:#77776c; }.cr-idea h2 { margin-bottom:7px; font-size:15px; }.cr-idea p { color:#828279; font-size:13px; line-height:1.65; }
.cr-team-cta { margin-top:30px; padding:26px; border-radius:10px; border:1px solid #e6e6dd; background:#f7f7f2; }.cr-team-cta > svg { color:#77776b; margin-bottom:12px; }.cr-team-cta h2 { font-size:17px; letter-spacing:-.02em; }.cr-team-cta p { margin:10px 0 20px; max-width:510px; color:#808075; font-size:13px; line-height:1.65; }
.cr-footer { margin-top:48px; padding-top:16px; color:#b2b2a9; font-size:10px; }
.cr-sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
@media(max-width:640px) {
  .cr-page { padding:18px 20px 24px; }.cr-header { margin-bottom:16px; }.cr-logo svg { width:78px; }.cr-header .cr-text-button { font-size:11px !important; }
  .cr-panel { padding-top:26px; }.cr-title-row { flex-wrap:wrap; gap:16px; margin-bottom:22px; }.cr-title-row > div:first-child { flex:1 1 200px; }.cr-period-controls { flex:1 1 auto; justify-content:flex-start; }.cr-month-label { flex:1; }.cr-month-label select { width:100%; max-width:100%; }
  .cr-code-strip { padding:18px; gap:16px; }.cr-code-strip p { max-width:180px; font-size:11px; }.cr-code { font-size:20px; }.cr-code-actions .cr-primary { padding:0 14px; }.cr-code-actions .cr-text-button { font-size:11px !important; }
  .cr-summary { gap:16px; }.cr-number { font-size:34px; }.cr-summary p { font-size:11px; }.cr-tiers { gap:8px; }.cr-tier > div { font-size:10px; }.cr-tier strong { font-size:15px; }
  .cr-table { font-size:12px; }.cr-table th,.cr-table td { padding:14px 5px; }.cr-table th:first-child { width:43%; }.cr-table th:last-child,.cr-table td:last-child { width:22%; }.cr-avatar { width:32px; height:32px; flex-basis:32px; }.cr-member { gap:8px; }.cr-muted { font-size:10px; }
  .cr-idea { gap:12px; }.cr-idea-icon { flex-basis:34px; height:34px; }.cr-team-cta { padding:22px; }
}
@media(max-width:380px) { .cr-code-strip { align-items:flex-start; flex-direction:column; }.cr-code-actions { flex-direction:row; gap:16px; }.cr-table th:first-child { width:43%; }.cr-table th:last-child { width:24%; } }
@media(prefers-reduced-motion:reduce) { .cr-progress > span { transition:none; } }
`;
